// src/components/Eventos/EventsHeats/utils/verticalPositionTracker.js
import { isSpecial } from './eventHelpers';

/**
 * Calcula las posiciones de los atletas en cada altura de un evento vertical
 * @param {Array} athletes - Array de atletas con sus resultados
 * @param {Array} heights - Array de alturas ordenadas
 * @returns {Object} - Objeto con posiciones y cambios por atleta y altura
 */
export const computeVerticalPositions = (athletes, heights) => {
  const result = {};
  
  // Crear estructura para almacenar rankings por altura
  const heightRankings = [];
  
  // Procesar cada altura
  for (let heightIndex = 0; heightIndex < heights.length; heightIndex++) {
    const currentHeight = heights[heightIndex];
    const heightKey = currentHeight.value.toFixed(2);
    
    // Recopilar el estado de cada atleta hasta esta altura (inclusive)
    const athleteStates = [];
    
    athletes.forEach(athlete => {
      const { idNumber, results } = athlete;
      
      // Calcular el estado del atleta hasta esta altura
      const athleteState = calculateAthleteStateUpToHeight(
        results, 
        heights, 
        heightIndex
      );
      
      athleteStates.push({
        idNumber,
        ...athleteState,
        heightIndex
      });
    });
    
    // Ordenar atletas según las reglas de eventos verticales
    const sortedAthletes = sortAthletesByVerticalRules(athleteStates);
    
    // Asignar posiciones
    const ranking = {};
    let currentPosition = 1;
    let tiedAthletes = [];
    
    for (let i = 0; i < sortedAthletes.length; i++) {
      const athlete = sortedAthletes[i];
      const nextAthlete = sortedAthletes[i + 1];
      
      // Agregar atleta al grupo de empate actual
      tiedAthletes.push(athlete);
      
      // Verificar si hay empate con el siguiente atleta
      const isTiedWithNext = nextAthlete && 
        areAthletesTied(athlete, nextAthlete);
      
      if (!isTiedWithNext) {
        // Asignar la misma posición a todos los atletas empatados
        tiedAthletes.forEach(tiedAthlete => {
          ranking[tiedAthlete.idNumber] = {
            position: athlete.hasValidClearance ? currentPosition : null,
            lastClearedHeight: tiedAthlete.lastClearedHeight,
            totalFailures: tiedAthlete.totalFailures,
            failuresAtLastHeight: tiedAthlete.failuresAtLastHeight,
            hasValidClearance: tiedAthlete.hasValidClearance,
            isEliminated: tiedAthlete.isEliminated
          };
        });
        
        // Actualizar posición para el siguiente grupo
        if (athlete.hasValidClearance) {
          currentPosition += tiedAthletes.length;
        }
        
        // Reiniciar grupo de empate
        tiedAthletes = [];
      }
    }
    
    heightRankings.push(ranking);
  }
  
  // Calcular cambios de posición para cada atleta
  athletes.forEach(athlete => {
    const { idNumber } = athlete;
    result[idNumber] = {
      positions: [],
      changes: [],
      heightStates: []
    };
    
    for (let i = 0; i < heights.length; i++) {
      const currentRanking = heightRankings[i];
      const previousRanking = i > 0 ? heightRankings[i - 1] : null;
      
      const currentPos = currentRanking[idNumber]?.position || null;
      const previousPos = previousRanking?.[idNumber]?.position || null;
      
      result[idNumber].positions.push(currentPos);
      result[idNumber].heightStates.push(currentRanking[idNumber] || {});
      
      // Calcular cambio de posición
      let change = null;
      if (currentPos && previousPos) {
        const diff = previousPos - currentPos;
        if (diff > 0) {
          change = { dir: 'up', val: diff };
        } else if (diff < 0) {
          change = { dir: 'down', val: Math.abs(diff) };
        } else {
          change = { dir: 'same', val: 0 };
        }
      }
      
      result[idNumber].changes.push(change);
    }
  });
  
  return result;
};

/**
 * Calcula el estado de un atleta hasta una altura específica
 * @param {Object} results - Resultados del atleta
 * @param {Array} heights - Array de alturas
 * @param {number} heightIndex - Índice de la altura actual
 * @returns {Object} - Estado del atleta
 */
function calculateAthleteStateUpToHeight(results, heights, heightIndex) {
  const heightsData = results.heights || {};
  
  let lastClearedHeight = null;
  let totalFailures = 0;
  let failuresAtLastHeight = 0;
  let hasValidClearance = false;
  let isEliminated = false;
  
  // Verificar estado especial
  if (results.specialStatus && results.specialStatus !== 'Valid') {
    return {
      lastClearedHeight: null,
      totalFailures: 0,
      failuresAtLastHeight: 0,
      hasValidClearance: false,
      isEliminated: true,
      specialStatus: results.specialStatus
    };
  }
  
  // Procesar alturas hasta el índice actual (inclusive)
  for (let i = 0; i <= heightIndex; i++) {
    const height = heights[i];
    const heightKey = height.value.toFixed(2);
    const heightResult = heightsData[heightKey];
    
    if (!heightResult || !heightResult.attempts) continue;
    
    const attempts = heightResult.attempts || [];
    const hasO = attempts.includes('O');
    const xCount = attempts.filter(a => a === 'X').length;
    const hasR = attempts.includes('R');
    
    // Si tiene 'O', actualizar última altura franqueada
    if (hasO) {
      lastClearedHeight = height.value;
      hasValidClearance = true;
      failuresAtLastHeight = attempts.indexOf('O'); // Intentos antes del 'O'
    }
    
    // Contar fallos
    totalFailures += xCount;
    
    // Si hay retiro (R), el atleta está eliminado para alturas futuras
    if (hasR) {
      isEliminated = true;
      break;
    }
    
    // Si hay 3 fallos consecutivos en una altura, eliminado
    if (xCount >= 3) {
      isEliminated = true;
      break;
    }
  }
  
  return {
    lastClearedHeight,
    totalFailures,
    failuresAtLastHeight,
    hasValidClearance,
    isEliminated
  };
}

/**
 * Ordena atletas según las reglas de eventos verticales
 * @param {Array} athleteStates - Estados de los atletas
 * @returns {Array} - Atletas ordenados
 */
function sortAthletesByVerticalRules(athleteStates) {
  return athleteStates.sort((a, b) => {
    // 1. Atletas eliminados van al final
    if (a.isEliminated && !b.isEliminated) return 1;
    if (!a.isEliminated && b.isEliminated) return -1;
    
    // 2. Si ambos están eliminados, ordenar por última altura franqueada
    if (a.isEliminated && b.isEliminated) {
      const heightA = a.lastClearedHeight || 0;
      const heightB = b.lastClearedHeight || 0;
      if (heightA !== heightB) return heightB - heightA;
      
      // Si misma altura, por menos fallos totales
      return a.totalFailures - b.totalFailures;
    }
    
    // 3. Atletas activos: ordenar por última altura franqueada
    const heightA = a.lastClearedHeight || 0;
    const heightB = b.lastClearedHeight || 0;
    
    if (heightA !== heightB) {
      return heightB - heightA; // Mayor altura primero
    }
    
    // 4. Misma altura: por menos fallos en esa altura
    if (a.failuresAtLastHeight !== b.failuresAtLastHeight) {
      return a.failuresAtLastHeight - b.failuresAtLastHeight;
    }
    
    // 5. Mismo número de fallos en última altura: por menos fallos totales
    return a.totalFailures - b.totalFailures;
  });
}

/**
 * Verifica si dos atletas están empatados
 * @param {Object} athleteA - Primer atleta
 * @param {Object} athleteB - Segundo atleta
 * @returns {boolean} - True si están empatados
 */
function areAthletesTied(athleteA, athleteB) {
  // Ambos eliminados
  if (athleteA.isEliminated && athleteB.isEliminated) {
    return athleteA.lastClearedHeight === athleteB.lastClearedHeight &&
           athleteA.totalFailures === athleteB.totalFailures;
  }
  
  // Uno eliminado y otro no
  if (athleteA.isEliminated !== athleteB.isEliminated) {
    return false;
  }
  
  // Ambos activos
  return athleteA.lastClearedHeight === athleteB.lastClearedHeight &&
         athleteA.failuresAtLastHeight === athleteB.failuresAtLastHeight &&
         athleteA.totalFailures === athleteB.totalFailures;
}

/**
 * Filtra alturas que tienen al menos un intento registrado para un atleta específico
 * @param {Object} athleteResults - Resultados del atleta específico
 * @param {Array} heights - Array de todas las alturas disponibles
 * @returns {Array} - Alturas filtradas para este atleta
 */
export const filterHeightsForAthlete = (athleteResults, heights) => {
  if (!athleteResults || !athleteResults.heights || !heights) return [];
  
  return heights.filter(height => {
    const heightKey = height.value.toFixed(2);
    const heightResult = athleteResults.heights[heightKey];
    
    if (!heightResult || !Array.isArray(heightResult.attempts)) return false;
    
    // Solo mostrar esta altura si el atleta tiene al menos un intento válido (no vacío)
    return heightResult.attempts.some(attempt => {
      return attempt && 
             typeof attempt === 'string' && 
             attempt.trim() !== '' && 
             attempt !== '-';
    });
  });
};

/**
 * Filtra alturas que tienen al menos un intento registrado (para uso global)
 * @param {Array} athletes - Array de atletas
 * @param {Array} heights - Array de alturas
 * @returns {Array} - Alturas filtradas
 */
export const filterHeightsWithAttempts = (athletes, heights) => {
  return heights.filter(height => {
    const heightKey = height.value.toFixed(2);
    
    // Verificar si algún atleta tiene intentos válidos (no vacíos) en esta altura
    return athletes.some(athlete => {
      const results = athlete.results || {};
      const heightsData = results.heights || {};
      const heightResult = heightsData[heightKey];
      
      if (!heightResult || !Array.isArray(heightResult.attempts)) return false;
      
      // Considerar que hay intentos válidos si hay algún valor que no sea vacío o undefined
      return heightResult.attempts.some(attempt => {
        // Verificar que el intento no sea null, undefined, string vacío o solo espacios
        return attempt && 
               typeof attempt === 'string' && 
               attempt.trim() !== '' && 
               attempt !== '-';
      });
    });
  });
};

/**
 * Obtiene las estadísticas de posición para un atleta específico
 * @param {Object} positionData - Datos de posición calculados
 * @param {string} athleteId - ID del atleta
 * @returns {Object} - Estadísticas de posición del atleta
 */
export const getAthleteVerticalPositionStats = (positionData, athleteId) => {
  const athleteData = positionData[athleteId];
  if (!athleteData) return null;
  
  const validPositions = athleteData.positions.filter(pos => pos !== null);
  if (validPositions.length === 0) return null;
  
  return {
    bestPosition: Math.min(...validPositions),
    worstPosition: Math.max(...validPositions),
    finalPosition: validPositions[validPositions.length - 1],
    totalChanges: athleteData.changes.filter(change => change !== null).length,
    positions: athleteData.positions,
    changes: athleteData.changes,
    heightStates: athleteData.heightStates
  };
};

/**
 * Formatea los datos de alturas con información de posición para un atleta específico
 * @param {Array} allHeights - Todas las alturas del evento
 * @param {Object} athleteResults - Resultados del atleta
 * @param {Object} positionStats - Estadísticas de posición del atleta
 * @returns {Array} - Alturas formateadas con datos de posición (solo las que tienen intentos)
 */
export const formatHeightsWithPositions = (allHeights, athleteResults, positionStats) => {
  if (!allHeights || !athleteResults) return [];
  
  // Filtrar alturas solo para este atleta específico
  const athleteHeights = filterHeightsForAthlete(athleteResults, allHeights);
  
  return athleteHeights.map((height, index) => {
    const heightKey = height.value.toFixed(2);
    const heightResult = athleteResults.heights?.[heightKey] || {
      attempts: height.isTiebreak ? [''] : ['', '', '']
    };
    
    // Encontrar el índice de esta altura en el array completo para obtener las estadísticas correctas
    const globalIndex = allHeights.findIndex(h => h.value === height.value);
    
    // Determinar si es la altura actual (siguiente después de la última franqueada)
    const lastClearedIndex = athleteHeights.findIndex(h => {
      const hKey = h.value.toFixed(2);
      const hResult = athleteResults.heights?.[hKey];
      return hResult && hResult.attempts && hResult.attempts.includes('O');
    });
    
    const isCurrentHeight = index === lastClearedIndex + 1;
    
    // Determinar si es la mejor altura (tiene al menos un 'O')
    const isBestHeight = heightResult.attempts && heightResult.attempts.includes('O');
    
    return {
      height: height.value,
      attempts: heightResult.attempts || (height.isTiebreak ? [''] : ['', '', '']),
      position: positionStats?.positions[globalIndex] || null,
      positionChange: positionStats?.changes[globalIndex] || null,
      isCurrentHeight,
      isBestHeight,
      isActive: isCurrentHeight,
      isTiebreak: height.isTiebreak || false
    };
  });
};